// The ops panel's API, served by the Go API under /api/v1/ops on the
// panel's own host. Every call carries the panel's session cookie.

export type Role = "member" | "admin";

export interface Account {
  id: string;
  /** Empty for an account that signs in only with SMS codes. */
  email: string;
  /** E.164, such as +8801712345678; empty when there is none. */
  phone: string;
  name: string;
  role: Role;
  two_step: boolean;
  disabled: boolean;
  disabled_at?: string;
  created_at: string;
  password_changed_at: string;
  last_sign_in_at?: string;
}

export interface SessionRow {
  id: string;
  site: string;
  device: string;
  ip?: string;
  created_at: string;
  expires_at: string;
}

export interface AccountEvent {
  kind: string;
  description: string;
  detail?: string;
  alarming: boolean;
  ip?: string;
  device?: string;
  created_at: string;
}

export interface AccountDetail {
  account: Account;
  recovery_codes_left: number;
  sessions: SessionRow[];
  events: AccountEvent[];
  clients: string[];
}

export type ClientKind = "group" | "customer";
export type ClientAccess = "everyone" | "listed";

export interface Client {
  id: string;
  name: string;
  kind: ClientKind;
  notes: string;
  access: ClientAccess;
  public: boolean;
  redirect_uris: string[];
  post_logout_redirect_uris: string[];
  disabled: boolean;
  disabled_at?: string;
  managed: boolean;
  created_at: string;
  updated_at: string;
}

export interface ListedAccount {
  id: string;
  email: string;
  phone: string;
  name: string;
  disabled: boolean;
}

export interface ClientDetail {
  client: Client;
  accounts: ListedAccount[];
  issuer: string;
  discovery_url: string;
}

export interface AuditEntry {
  id: string;
  actor_id?: string;
  actor: string;
  action: string;
  target_kind?: string;
  target_id?: string;
  detail?: string;
  ip?: string;
  created_at: string;
}

export interface Overview {
  accounts: { total: number; admins: number; disabled: number; two_step: number };
  clients: { total: number; active: number; customer: number };
  recent: AuditEntry[];
}

export interface Me {
  account: Account;
  expires_at: string;
}

export interface SMSField {
  name: string;
  label: string;
  help?: string;
  required: boolean;
  default?: string;
}

export interface SMSProvider {
  name: string;
  label: string;
  options: SMSField[];
  secrets: SMSField[];
  development?: boolean;
}

export interface SMSSettings {
  provider: string;
  enabled: boolean;
  options: Record<string, string>;
  /** A stored secret comes back as UNCHANGED, never as its value. */
  secrets: Record<string, string>;
  updatedAt: string;
}

export interface SMSRecord {
  id: string;
  to: string;
  purpose: string;
  provider: string;
  status: "sent" | "failed";
  error?: string;
  createdAt: string;
}

export interface SMSView {
  providers: SMSProvider[];
  settings: SMSSettings | null;
  ready: boolean;
  allow_development: boolean;
  messages: SMSRecord[];
}

/** Stands in for a stored secret; sending it back keeps the stored value. */
export const UNCHANGED = "••••••••";

/** How an account is named where it has no email. */
export const identity = (a: { email: string; phone: string }) => a.email || a.phone;

export interface Settings {
  issuer: string;
  discovery_url: string;
  panel_url: string;
}

/** An answer other than 2xx, carrying the API's error envelope. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string>;

  constructor(status: number, code: string, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

const BASE = "/api/v1/ops";

/** Dispatched on window whenever the API says the panel is signed out. */
export const SIGNED_OUT = "ops:signed-out";

export async function api<T>(method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE", path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      method,
      cache: "no-store",
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network", "The API did not answer. Check your connection and try again.");
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const e = data?.error ?? {};
    // The panel's session ended (signed out at id.mashriqain.com, or it
    // expired): the app shows the sign-in screen again.
    if (res.status === 401) window.dispatchEvent(new Event(SIGNED_OUT));
    throw new ApiError(res.status, e.code ?? "unknown", e.message ?? `The API answered ${res.status}.`, e.fields);
  }
  return data as T;
}

export const get = <T>(path: string) => api<T>("GET", path);

/** Where the browser goes to sign in, returning to `next` on the panel. */
export function signInURL(next: string): string {
  return `${BASE}/login?next=${encodeURIComponent(next)}`;
}
